import Button from '@/common/components/base/button/Button';
import DateInput from '@/common/components/base/input/dateInput/DateInput';
import RadioGroup from '@/common/components/base/input/radioGroup/RadioGroup';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import { PERSIAN_LATTER_RULE } from '@/common/components/base/input/textInput/textInput.rules';
import Typography from '@/common/components/base/typography/Typography';
import { OwnerConstType } from '@/common/types/Files.type';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import { NATIONAL_CODE_RULE } from '@/common/utils/rules/nationalCode.rules';
import { requiredText } from '@/common/utils/rules/publicRules';
import { ADJUSTER_REPORT_DAMAGED_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/damaged/damagedLinks';
import { validateNotFutureDate } from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import { getCulpritUserInformationQueryOption } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getCulpritUserInformation/getCulpritUserInformationQueryOptions';
import { getDamagedUserInformationQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getDamagedUserInformation/getDamagedUserInformationQueryOptions';
import { PatchDamagedUserInformationQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchDamagedUserInformation/patchDamagedUserInformationQueryOptions';
import { PersonalInformationOwnerType } from '@/modules/adjusterReportModules/IncidentParticipants/types/OwnerType';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';

export const OwnerUserInformation = () => {
  const [searchParams] = useSearchParams();
  const { id, damagedId } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { data } = useQuery(
    searchParams.get('type') === 'culprit'
      ? getCulpritUserInformationQueryOption(+id!)
      : getDamagedUserInformationQueryOptions(+id!, +damagedId!),
  );
  const mutate = useMutation(PatchDamagedUserInformationQueryOptions());

  const methods = useForm<PersonalInformationOwnerType>({
    mode: 'onChange',
  });
  const submitHandler = () => {
    const temporaryObject: PersonalInformationOwnerType = {};
    if (ownerType === 'legal') {
      temporaryObject.owner_type = methods.getValues(
        'owner_type',
      ) as OwnerConstType;
      temporaryObject.name = methods.getValues('name');
      temporaryObject.registration_number = methods.getValues(
        'registration_number',
      );
      temporaryObject.national_id = methods.getValues('national_id');
      temporaryObject.economic_code = methods.getValues('economic_code');
    } else {
      temporaryObject.owner_type = methods.getValues(
        'owner_type',
      ) as OwnerConstType;
      temporaryObject.first_name = methods.getValues('first_name');
      temporaryObject.last_name = methods.getValues('last_name');
      temporaryObject.father_name = methods.getValues('father_name');
      temporaryObject.national_code = methods.getValues('national_code');
      temporaryObject.identity_number =
        methods.getValues('identity_number') &&
        methods.getValues('identity_number') !== ''
          ? methods.getValues('identity_number')
          : null;
      temporaryObject.identity_issue_location =
        methods.getValues('identity_issue_location') &&
        methods.getValues('identity_issue_location') !== ''
          ? methods.getValues('identity_issue_location')
          : null;
      temporaryObject.birthdate = convertPersianToGregorian(
        methods.getValues('birthdate') as string,
      );
      temporaryObject.gender = methods.getValues('gender');
      temporaryObject.owner_type = methods.getValues(
        'owner_type',
      ) as OwnerConstType;
    }
    mutate.mutate(
      { id: id!, formData: temporaryObject, damaged_id: damagedId! },
      {
        onSuccess: () => {
          navigate(
            ADJUSTER_REPORT_DAMAGED_LINKS.DAMAGED_INFORMATION({
              id: +id!,
              damagedId: +damagedId!,
            }).DRIVER_OWNER_INFORMATION.ownerContact,
            { replace: true, relative: 'path' },
          );
        },
      },
    );
  };

  useEffect(() => {
    if (data) {
      methods.reset({
        ...data.personal_information,
      });
    }
  }, [data]);
  useEffect(() => {
    client.invalidateQueries({
      queryKey:
        searchParams.get('type') === 'culprit'
          ? getCulpritUserInformationQueryOption(+id!).queryKey
          : getDamagedUserInformationQueryOptions(+id!, +damagedId!).queryKey,
    });
  }, [damagedId, id, searchParams, client]);

  const { owner_type: ownerType } = methods.getValues();
  const ownerTypeWatcher = useWatch({
    control: methods?.control,
    name: 'owner_type',
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(submitHandler)}>
        <Flex flexDirection="column" gap="20px">
          <React.Fragment>
            <RadioGroup
              className={css({
                flexDirection: 'row',
                gap: '2rem',
              })}
              title="نوع مالکیت"
              rules={{ required: 'نوع مالکیت را وارد کنید' }}
              name="owner_type"
              options={[
                { label: 'حقیقی', value: 'real' },
                { label: 'حقوقی', value: 'legal' },
              ]}
              renderRadioButton={(radioButton, value) => (
                <Box key={value} display="flex" alignItems="center" gap="10px">
                  {radioButton}
                </Box>
              )}
            />
            <Typography textStyle="b2" color={'red-3'}>
              {methods.formState.errors['owner_type']?.message}
            </Typography>
          </React.Fragment>
          {ownerType === 'real' || ownerTypeWatcher === 'real' ? (
            <>
              <React.Fragment>
                <RadioGroup
                  className={css({
                    flexDirection: 'row',
                    gap: '2rem',
                  })}
                  title="جنسیت مالک"
                  name="gender"
                  options={[
                    { label: 'اقا', value: 'male' },
                    { label: 'خانم', value: 'female' },
                  ]}
                  rules={{ required: 'لطفا جنسیت مالک را انتخاب کنید' }}
                  renderRadioButton={(radioButton, value) => (
                    <Box
                      key={value}
                      display="flex"
                      alignItems="center"
                      gap="10px"
                    >
                      {radioButton}
                    </Box>
                  )}
                />
                <Typography textStyle="b2" color={'red'}>
                  {methods.formState.errors['gender']?.message}
                </Typography>
              </React.Fragment>
              <TextInput
                rules={{
                  required: 'وارد کردن نام اجباری است',
                  ...PERSIAN_LATTER_RULE,
                }}
                name="first_name"
                label="نام"
                key={'first_name'}
              />
              <TextInput
                rules={{
                  required: 'وارد کردن نام خانوادگی است',
                  ...PERSIAN_LATTER_RULE,
                }}
                name="last_name"
                label="نام خانوادگی"
                key={'last_name'}
              />
              <DateInput
                buttonText="ثبت"
                modalLabel="تاریخ تولد"
                rules={{
                  required: requiredText,
                  validate: validateNotFutureDate,
                }}
                name="birthdate"
                label="تاریخ تولد"
                key={'birthdate'}
              />
              <TextInput
                rules={{
                  required: { message: requiredText, value: true },
                  ...NATIONAL_CODE_RULE,
                }}
                name="national_code"
                max={10}
                label="کد ملی"
                noCharInput
                key={'national_code'}
              />
              <TextInput
                name="father_name"
                label="نام پدر"
                optionalLabel
                rules={{ ...PERSIAN_LATTER_RULE }}
              />
              <TextInput
                name="identity_number"
                label="شماره شناسنامه"
                rules={{
                  pattern: {
                    value: /^[0-9]*$/,
                    message: 'شماره شناسنامه نامعتبر است',
                  },
                }}
                optionalLabel
                noCharInput
                key={'identity_number'}
              />
              <TextInput
                name="identity_issue_location"
                label="محل صدور شناسنامه"
                optionalLabel
                key={'identity_issue_location'}
              />
            </>
          ) : (
            <>
              <TextInput
                rules={{
                  required: 'وارد کردن نام اجباری است',
                  ...PERSIAN_LATTER_RULE,
                }}
                name="name"
                label="نام"
                key={'name'}
              />
              <TextInput
                rules={{ required: 'وارد کردن شماره ثبت اجباری است' }}
                name="registration_number"
                label="شماره ثبت"
                key={'registration_number'}
              />
              <TextInput
                name="economic_code"
                label="کد اقتصادی"
                rules={{ required: 'وارد کردن کد اقتصادی اجباری است' }}
                key={'economic_code'}
              />
              <TextInput
                name="national_id"
                label="شناسه ملی"
                rules={{ required: 'وارد کردن شناسه ملی اجباری است' }}
              />
            </>
          )}
          <Button
            onClick={methods.handleSubmit(submitHandler)}
            type="submit"
            width="100%"
            disabled={!methods?.formState?.isValid}
          >
            تائید و ادامه
          </Button>
        </Flex>
      </form>
    </FormProvider>
  );
};
